import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router";
import { useAuth, useCan } from "../auth/AuthProvider";
import { Icon } from "../components/icons";
import { Pagination } from "../components/Pagination";
import {
  Alert,
  Card,
  CardHeader,
  LoadingBlock,
  Mono,
  PageHeader,
  StatusBadge,
  Table,
  Td,
  Th,
  buttonStyles,
  cx,
} from "../components/ui";
import { QuotaMeter } from "../components/Quota";
import { absoluteDay } from "../lib/format";
import { useRegisteredDomains } from "../lib/queries";
import { daysUntil, domainStatus, yearFraction } from "./domain/status";

/**
 * Every domain the organization registered through the platform.
 *
 * Distinct from an app's Domains tab, which attaches a name the customer
 * already owns. These are names bought here: each row is a renewal date the
 * balance is responsible for, which is why the renewal column is the one that
 * carries a warning rather than the status column.
 */
/* Twenty, like tunnels: a row is one line and a page should not scroll far. */
const PER_PAGE = 20;

export function DomainsPage() {
  const { t } = useTranslation("domains");
  const { currentOrg } = useAuth();
  const orgId = currentOrg?.id;
  const canManage = useCan("admin");
  const [page, setPage] = useState(1);
  const domains = useRegisteredDomains(orgId, { limit: PER_PAGE, offset: (page - 1) * PER_PAGE });

  // Left behind by a purchase on the new page, which navigates here to show it.
  const location = useLocation();
  const notice = (location.state as { notice?: string } | null)?.notice ?? null;

  const rows = domains.data?.data ?? [];
  const total = domains.data?.meta.total ?? 0;

  return (
    <div className="space-y-6">
      <PageHeader
        title={t("title")}
        description={t("description")}
        action={
          canManage && total > 0 ? (
            <div className="flex flex-col items-end gap-1.5">
              <Link to="/domains/new" className={buttonStyles("primary")}>
                <Icon name="plus" className="size-3.5" />
                {t("create")}
              </Link>
              <QuotaMeter orgId={orgId} resource="domains" />
            </div>
          ) : undefined
        }
      />

      {notice && <Alert tone="success">{notice}</Alert>}
      {domains.error && <Alert>{domains.error.message}</Alert>}

      {domains.isPending ? (
        <Card>
          <LoadingBlock />
        </Card>
      ) : total === 0 ? (
        <GettingStarted canManage={canManage} />
      ) : (
        <Card>
          <Table
            head={
              <>
                <Th>{t("columns.domain")}</Th>
                <Th>{t("columns.status")}</Th>
                <Th priority="sm">{t("columns.renews")}</Th>
                <Th priority="md">{t("columns.renewal")}</Th>
                <Th priority="lg">{t("columns.dns")}</Th>
              </>
            }
          >
            {rows.map((domain) => {
              const status = domainStatus(domain.status);
              const days = daysUntil(domain.renewalDate);
              /* The one row state that needs acting on: the sweep tried, the
                 balance was short, and the date is coming. */
              const short = domain.status === "active" && domain.renewalError !== null && domain.autorenew;
              return (
                <tr key={domain.id} className="transition-colors hover:bg-raised/50">
                  <Td>
                    <Link to={`/domains/${domain.id}`} className="font-medium text-fg hover:underline">
                      <Mono className="text-[13px] text-fg">{domain.domain}</Mono>
                    </Link>
                    {domain.kind === "transfer" && domain.status === "pending" && (
                      <div className="mt-0.5 text-[11px] text-fg-subtle">{t("transferredIn")}</div>
                    )}
                  </Td>
                  <Td>
                    <StatusBadge
                      tone={status.tone}
                      label={t(`status.${status.key}.label`)}
                      title={t(`status.${status.key}.hint`)}
                    />
                  </Td>
                  <Td priority="sm" className="text-fg-muted">
                    {domain.renewalDate && days !== null ? (
                      <>
                        <div className={cx("tabular-nums", days <= 30 ? "text-fg" : undefined)}>{absoluteDay(domain.renewalDate)}</div>
                        {/* How much of the year is gone, as a hairline: a
                            column of dates is read one at a time, a column
                            of bars at a glance. */}
                        <div className="mt-1.5 flex items-center gap-2">
                          <span className="relative h-0.5 w-16 overflow-hidden rounded-full bg-raised">
                            <span
                              className={cx("absolute inset-y-0 left-0 rounded-full", short ? "bg-danger" : days <= 30 ? "bg-warn" : "bg-mint")}
                              style={{ width: `${Math.round(yearFraction(domain.registeredAt, domain.renewalDate) * 100)}%` }}
                            />
                          </span>
                          <span className="text-[11px] tabular-nums text-fg-subtle">{t("daysLeft", { count: Math.max(0, days) })}</span>
                        </div>
                      </>
                    ) : (
                      <span className="text-fg-subtle">—</span>
                    )}
                  </Td>
                  <Td priority="md" className="text-fg-muted">
                    {short ? (
                      <span className="text-danger">{t("renewal.short")}</span>
                    ) : domain.autorenew ? (
                      t("renewal.auto")
                    ) : (
                      t("renewal.manual")
                    )}
                  </Td>
                  <Td priority="lg" className="text-fg-muted">
                    {domain.managedDns ? t("dns.hosted") : t("dns.elsewhere")}
                  </Td>
                </tr>
              );
            })}
          </Table>
          <div className="border-t border-line px-4 py-3 text-[13px] text-fg-muted">
            {t("total", { count: total })}
          </div>
          {/* Renders nothing on a single page. */}
          <Pagination total={total} perPage={PER_PAGE} page={page} onChange={setPage} />
        </Card>
      )}
    </div>
  );
}

/**
 * The empty state, which is where the shape of the feature gets explained:
 * search, buy from credit, point at an app. It closes with the two other
 * ways in — transfer, or attach without moving — because a customer with a
 * name already has a decision to make before a search box is useful.
 */
function GettingStarted({ canManage }: { canManage: boolean }) {
  const { t } = useTranslation("domains");
  const steps: { title: string; detail: string }[] = t("gettingStarted.steps", { returnObjects: true });

  return (
    <Card>
      <CardHeader title={t("gettingStarted.title")} description={t("gettingStarted.description")} />
      <ol className="grid gap-px border-b border-line bg-line md:grid-cols-3">
        {steps.map((step, i) => (
          <li key={step.title} className="min-w-0 bg-surface p-5">
            <p className="micro text-fg-subtle">{t("gettingStarted.step", { number: i + 1 })}</p>
            <p className="mt-2 text-sm font-medium text-fg">{step.title}</p>
            <p className="mt-1 text-sm leading-6 text-fg-muted">{step.detail}</p>
          </li>
        ))}
      </ol>
      <div className="flex flex-wrap items-center justify-between gap-4 px-5 py-4">
        <p className="max-w-xl text-xs leading-5 text-fg-subtle">{t("gettingStarted.note")}</p>
        {canManage && (
          <Link to="/domains/new" className={buttonStyles("primary")}>
            {t("create")}
          </Link>
        )}
      </div>
    </Card>
  );
}
